import React, { useState, useEffect } from 'react'
import { X, Search, Grid, List, Heart, Trash2, Eye, EyeOff, Filter, RefreshCw, Calendar, Star, MessageCircle, Mic } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { getUserImages, deleteImage } from '../../lib/supabase'
import { checkDatabaseStatus, runDatabaseSetup, DatabaseSetupResult } from '../../lib/databaseSetup'
import ImageViewer from './ImageViewer'
import DeleteConfirmModal from './DeleteConfirmModal'
import VoicePersonaChat from '../Voice/VoicePersonaChat'
import VoicePersonaCreator from '../Voice/VoicePersonaCreator'
import { VoicePersona } from '../../types'

interface ImageHistoryProps {
  isOpen: boolean
  onClose: () => void
  newGenerations?: any[]
  inline?: boolean
}

interface ImageItem {
  id: string
  title: string
  description?: string
  prompt_text: string
  image_url: string
  filters_used: Record<string, any>
  is_public: boolean
  is_nsfw: boolean
  likes_count: number
  views_count: number
  created_at: string
  updated_at: string
}

const ImageHistory: React.FC<ImageHistoryProps> = ({ isOpen, onClose, newGenerations = [], inline = false }) => {
  const { user } = useAuth()
  const [images, setImages] = useState<ImageItem[]>([])
  const [filteredImages, setFilteredImages] = useState<ImageItem[]>([])
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set())
  const [isLoading, setIsLoading] = useState(true)
  const [isSetupNeeded, setIsSetupNeeded] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'popular'>('newest')
  const [filterBy, setFilterBy] = useState<'all' | 'public' | 'private' | 'favorites'>('all')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [selectedImage, setSelectedImage] = useState<ImageItem | null>(null)
  const [imageToDelete, setImageToDelete] = useState<ImageItem | null>(null)
  const [isSettingUp, setIsSettingUp] = useState(false)
  const [setupResult, setSetupResult] = useState<DatabaseSetupResult | null>(null)
  
  useEffect(() => {
    if (isOpen && user) {
      loadImages()
    }
  }, [isOpen, user])

  useEffect(() => {
    filterAndSortImages()
  }, [images, searchQuery, sortBy, filterBy])

  const loadImages = async () => {
    if (!user) {
      // If no user, use newGenerations as fallback
      setImages(newGenerations || [])
      setIsLoading(false)
      return
    }

    try {
      const userImages = await getUserImages(user.id)
      if (userImages && userImages.length > 0) {
        // Database has images
        setImages([...newGenerations, ...userImages])
        setIsSetupNeeded(false)
      } else {
        // Database is empty or tables don't exist, check if it's a setup issue
        const dbStatus = await checkDatabaseStatus()
        if (!dbStatus.isSetup) {
          // Tables don't exist, show setup message but also show newGenerations
          setIsSetupNeeded(true)
          setImages(newGenerations || [])
        } else {
          // Tables exist but no images yet
          setImages(newGenerations || [])
          setIsSetupNeeded(false)
        }
      }
    } catch (error) {
      console.error('Error loading user images:', error)
      // On error, fall back to newGenerations
      setImages(newGenerations || [])
      setIsSetupNeeded(true)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDatabaseSetup = async () => {
    if (!user) {
      setSetupResult({
        success: false,
        message: 'Please log in to run database setup',
        error: 'Authentication required'
      })
      return
    }

    setIsSettingUp(true)
    setSetupResult(null)
    
    try {
      console.log('🚀 Running automated database setup...')
      const result = await runDatabaseSetup()
      setSetupResult(result)
      
      if (result.success) {
        console.log('✅ Database setup successful, reloading images...')
        // Wait a moment for the database to be ready
        setTimeout(() => {
          setIsSetupNeeded(false)
          loadImages()
        }, 2000)
      }
    } catch (error) {
      console.error('❌ Database setup failed:', error)
      setSetupResult({
        success: false,
        message: 'Database setup failed with unexpected error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
    } finally {
      setIsSettingUp(false)
    }
  }

  const filterAndSortImages = () => {
    let filtered = [...images]

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(img => 
        img.prompt_text.toLowerCase().includes(searchQuery.toLowerCase()) ||
        img.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (img.description && img.description.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    }

    // Apply category filter
    switch (filterBy) {
      case 'public':
        filtered = filtered.filter(img => img.is_public)
        break
      case 'private':
        filtered = filtered.filter(img => !img.is_public)
        break
      case 'favorites':
        filtered = filtered.filter(img => favoriteIds.has(img.id))
        break
    }

    // Apply sorting
    switch (sortBy) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        break
      case 'oldest':
        filtered.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
        break
      case 'popular':
        filtered.sort((a, b) => (b.likes_count + b.views_count) - (a.likes_count + a.views_count))
        break
    }

    setFilteredImages(filtered)
  }

  const handleDeleteImage = (image: ImageItem) => {
    setImageToDelete(image)
  }

  const handleToggleFavorite = (imageId: string) => {
    // Toggle favorite state locally
    setFavoriteIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(imageId)) {
        newSet.delete(imageId)
      } else {
        newSet.add(imageId)
      }
      return newSet
    })
  }

  const handleImageUpdate = (updatedImage: ImageItem) => {
    // Update the image in the images state
    setImages(prev => prev.map(img => 
      img.id === updatedImage.id ? updatedImage : img
    ))
    
    // Update the selectedImage if it's the same image
    if (selectedImage?.id === updatedImage.id) {
      setSelectedImage(updatedImage)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getFilterCount = (filters: Record<string, any>) => {
    return Object.values(filters).filter(value => value !== '' && value !== null).length
  }

  if (!isOpen) return null

  const content = (
    <div className={inline ? "space-y-6" : "bg-white rounded-t-3xl md:rounded-2xl shadow-xl w-full h-full md:w-full md:max-w-6xl md:h-[90vh] flex flex-col"}>
      {/* Header */}
      <div className={`flex items-center justify-between p-4 ${inline ? "" : "border-b border-gray-200 bg-white rounded-t-3xl md:rounded-t-2xl"}`}>
        <div className="flex items-center space-x-3">
          <h2 className="text-xl font-bold text-gray-900">Generation History</h2>
          <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-sm font-medium">
            {filteredImages.length} images
          </span>
        </div>
        {!inline && (
          <button 
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Controls */}
      <div className={`p-4 ${inline ? "bg-white rounded-lg border border-gray-200" : "border-b border-gray-200 bg-gray-50"}`}>
        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search prompts, titles, descriptions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Filters and View Controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {/* Sort */}
            <select 
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="popular">Most Popular</option>
            </select>

            {/* Filter */}
            <select 
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value as any)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Images</option>
              <option value="public">Public Only</option>
              <option value="private">Private Only</option>
              <option value="favorites">Favorites</option>
            </select>
          </div>

          {/* View Mode */}
          <div className="flex items-center space-x-1 bg-white rounded-lg border border-gray-300 p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-primary-500 text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${viewMode === 'list' ? 'bg-primary-500 text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={inline ? "flex-1" : "flex-1 overflow-y-auto p-4"}>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin"></div>
            <span className="ml-3 text-gray-600">Loading your creations...</span>
          </div>
        ) : isSetupNeeded ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-center max-w-2xl">
              <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                <Filter className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No images generated yet</h3>
              <p className="text-gray-600 mb-6">Start creating to see your generation history!</p>
              
              {/* Database Setup Section */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-6">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                    <RefreshCw className="w-6 h-6 text-yellow-600" />
                  </div>
                </div>
                
                <h4 className="text-lg font-semibold text-yellow-800 mb-2">🚨 Database Setup Required</h4>
                <p className="text-yellow-700 mb-4">
                  <strong>Your Generation History is empty because database tables haven't been created yet.</strong> 
                  This is a quick 2-minute fix that will unlock all your image history, favorites, and user features.
                </p>
                
                {/* Automated Setup Button */}
                <div className="space-y-4">
                  <button
                    onClick={handleDatabaseSetup}
                    disabled={isSettingUp}
                    className="w-full bg-yellow-600 hover:bg-yellow-700 disabled:bg-yellow-400 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center space-x-2"
                  >
                    {isSettingUp ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                        <span>Checking database...</span>
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-5 h-5" />
                        <span>🔍 Check Database Status</span>
                      </>
                    )}
                  </button>
                  
                  {setupResult && (
                    <div className={`p-4 rounded-lg ${
                      setupResult.success 
                        ? 'bg-green-50 border border-green-200 text-green-800' 
                        : 'bg-red-50 border border-red-200 text-red-800'
                    }`}>
                      <p className="font-medium">{setupResult.message}</p>
                      {setupResult.error && (
                        <p className="text-sm mt-1 opacity-75">{setupResult.error}</p>
                      )}
                      {setupResult.success && setupResult.tablesCreated && (
                        <p className="text-sm mt-1 opacity-75">
                          Tables created: {setupResult.tablesCreated.join(', ')}
                        </p>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="mt-6 text-sm text-yellow-700">
                  <p><strong>⚡ Quick Fix (2 minutes):</strong></p>
                  <div className="mt-2 space-y-1 text-left">
                    <p>1. Open <a href="https://supabase.com/dashboard/project/zuyvaptdwlnapdstqndu" target="_blank" rel="noopener noreferrer" className="underline hover:text-yellow-800">Supabase Dashboard → zuyvaptdwlnapdstqndu</a></p>
                    <p>2. Go to <strong>SQL Editor</strong> → <strong>New Query</strong></p>
                    <p>3. Copy & run <code className="bg-yellow-100 px-1 rounded">supabase/migrations/001_create_tables.sql</code></p>
                    <p>4. Refresh this page and your images will appear!</p>
                  </div>
                </div>
                
                <div className="mt-4 flex items-center justify-center space-x-4 text-sm">
                  <div className="flex items-center text-green-600">
                    <span className="text-lg mr-1">📚</span>
                    <span>Need help? See <code>setup_database.md</code> for step-by-step instructions</span>
                  </div>
                </div>
                
                <div className="mt-4 text-xs text-yellow-600 bg-yellow-100 p-3 rounded">
                  <p><strong>✅ After setup:</strong> All future images will save automatically to your account</p>
                </div>
              </div>
            </div>
          </div>
        ) : filteredImages.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery ? 'No matching images found' : 'No images generated yet'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchQuery 
                ? 'Try adjusting your search terms' 
                : 'Start creating to see your generation history!'
              }
            </p>
            
            {!searchQuery && (
              <div className="max-w-lg mx-auto p-6 bg-amber-50 rounded-xl border-2 border-amber-200">
                <div className="flex items-center justify-center mb-3">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <Star className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
                <h4 className="font-semibold text-amber-900 mb-2 text-lg">🚨 Database Setup Required</h4>
                <p className="text-sm text-amber-800 mb-4 leading-relaxed">
                  <strong>Your Generation History is empty because database tables haven't been created yet.</strong>
                  This is a quick 2-minute fix that will unlock all your image history, favorites, and user features.
                </p>
                <div className="text-sm text-amber-700 bg-amber-100 p-4 rounded-lg text-left mb-4">
                  <p className="font-semibold mb-2">⚡ Quick Fix (2 minutes):</p>
                  <div className="space-y-1">
                    <p>1. Open <strong>Supabase Dashboard</strong> → zuyvaptdwlnapdstqndu</p>
                    <p>2. Go to <strong>SQL Editor</strong> → New Query</p>
                    <p>3. Copy & run <code className="bg-amber-200 px-1 rounded">supabase/migrations/001_create_tables.sql</code></p>
                    <p>4. Refresh this page and your images will appear!</p>
                  </div>
                </div>
                <div className="text-xs text-amber-700 space-y-1">
                  <p>📋 <strong>Need help?</strong> See <code className="bg-amber-200 px-1 rounded">setup_database.md</code> for step-by-step instructions</p>
                  <p>✅ <strong>After setup:</strong> All future images will save automatically to your account</p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className={viewMode === 'grid' 
            ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3'
            : 'space-y-3'
          }>
            {filteredImages.map((image) => (
              <div key={image.id} className={viewMode === 'grid' ? 'group' : 'group'}>
                {viewMode === 'grid' ? (
                  // Grid View
                  <div className="relative aspect-square bg-gray-100 rounded-xl overflow-hidden">
                    <img
                      src={image.image_url}
                      alt={image.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200 cursor-pointer"
                      onClick={() => setSelectedImage(image)}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        // Replace broken image with placeholder
                        target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="300" viewBox="0 0 300 300"%3E%3Crect width="300" height="300" fill="%23f3f4f6"/%3E%3Ctext x="150" y="130" text-anchor="middle" fill="%23d1d5db" font-family="Arial, sans-serif" font-size="16"%3EImage Unavailable%3C/text%3E%3Ctext x="150" y="150" text-anchor="middle" fill="%23d1d5db" font-family="Arial, sans-serif" font-size="12"%3ETempporary link expired%3C/text%3E%3Ctext x="150" y="170" text-anchor="middle" fill="%23d1d5db" font-family="Arial, sans-serif" font-size="12"%3EGenerate new image%3C/text%3E%3C/svg%3E'
                        target.style.filter = 'grayscale(100%)'
                        console.warn('🖼️ Broken image detected:', image.image_url)
                      }}
                    />
                    
                    {/* Overlay with actions */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <div className="flex space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedImage(image)
                          }}
                          className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                        >
                          <Eye className="w-4 h-4 text-gray-700" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleToggleFavorite(image.id)
                          }}
                          className={`p-2 rounded-full transition-colors ${
                            favoriteIds.has(image.id) 
                              ? 'bg-red-500 text-white' 
                              : 'bg-white text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <Heart className={`w-4 h-4 ${favoriteIds.has(image.id) ? 'fill-current' : ''}`} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteImage(image)
                          }}
                          className="p-2 bg-white rounded-full hover:bg-red-50 hover:text-red-600 transition-colors"
                        >
                          <Trash2 className="w-4 h-4 text-gray-700" />
                        </button>
                      </div>
                    </div>

                    {/* Status indicators */}
                    <div className="absolute top-2 right-2 flex space-x-1">
                      {image.is_public && (
                        <div className="p-1 bg-green-500 rounded-full">
                          <Eye className="w-3 h-3 text-white" />
                        </div>
                      )}
                      {favoriteIds.has(image.id) && (
                        <div className="p-1 bg-red-500 rounded-full">
                          <Heart className="w-3 h-3 text-white fill-current" />
                        </div>
                      )}
                    </div>

                    {/* Filter count */}
                    {getFilterCount(image.filters_used) > 0 && (
                      <div className="absolute bottom-2 left-2">
                        <span className="px-2 py-1 bg-black bg-opacity-70 text-white text-xs rounded-full">
                          {getFilterCount(image.filters_used)} filters
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  // List View
                  <div className="flex items-center space-x-4 p-4 bg-white rounded-xl border border-gray-200 hover:border-gray-300 transition-colors">
                    <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={image.image_url}
                        alt={image.title}
                        className="w-full h-full object-cover cursor-pointer"
                        onClick={() => setSelectedImage(image)}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          // Replace broken image with placeholder
                          target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"%3E%3Crect width="100" height="100" fill="%23f3f4f6"/%3E%3Ctext x="50" y="45" text-anchor="middle" fill="%23d1d5db" font-family="Arial, sans-serif" font-size="8"%3EImage%3C/text%3E%3Ctext x="50" y="55" text-anchor="middle" fill="%23d1d5db" font-family="Arial, sans-serif" font-size="8"%3EUnavailable%3C/text%3E%3C/svg%3E'
                          target.style.filter = 'grayscale(100%)'
                          console.warn('🖼️ Broken image detected:', image.image_url)
                        }}
                      />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 truncate">{image.title}</h3>
                      <p className="text-sm text-gray-500 truncate mt-1">{image.prompt_text}</p>
                      <div className="flex items-center space-x-4 mt-2 text-xs text-gray-400">
                        <span>{formatDate(image.created_at)}</span>
                        <span>{getFilterCount(image.filters_used)} filters</span>
                        <span>{image.views_count} views</span>
                        <div className="flex items-center space-x-1">
                          {image.is_public ? (
                            <Eye className="w-3 h-3" />
                          ) : (
                            <EyeOff className="w-3 h-3" />
                          )}
                          <span>{image.is_public ? 'Public' : 'Private'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleToggleFavorite(image.id)}
                        className={`p-2 rounded-full transition-colors ${
                          favoriteIds.has(image.id) 
                            ? 'bg-red-50 text-red-500' 
                            : 'bg-gray-50 text-gray-400 hover:text-red-500'
                        }`}
                      >
                        <Heart className={`w-4 h-4 ${favoriteIds.has(image.id) ? 'fill-current' : ''}`} />
                      </button>
                      <button
                        onClick={() => handleDeleteImage(image)}
                        className="p-2 bg-gray-50 text-gray-400 hover:text-red-500 rounded-full transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Image Viewer Modal */}
      {selectedImage && (
        <ImageViewer
          image={selectedImage}
          onClose={() => setSelectedImage(null)}
          onDelete={handleDeleteImage}
          onToggleFavorite={() => handleToggleFavorite(selectedImage.id)}
          isFavorited={favoriteIds.has(selectedImage.id)}
          onImageUpdate={handleImageUpdate}
        />
      )}

      {/* Delete Confirmation Modal */}
      {imageToDelete && (
        <DeleteConfirmModal
          image={imageToDelete}
          onConfirm={async () => {
            try {
              await deleteImage(imageToDelete.id)
              setImages(prev => prev.filter(img => img.id !== imageToDelete.id))
              setImageToDelete(null)
            } catch (error) {
              console.error('Error deleting image:', error)
            }
          }}
          onCancel={() => setImageToDelete(null)}
        />
      )}
    </div>
  )

  return inline ? content : (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      {content}
    </div>
  )
}

export default ImageHistory 